// Libraries
import React from 'react';
import moment from 'moment';
import { useParams } from 'react-router-dom';
// Hooks
import useLoadPatientConsentStatus from '../../../hooks/services/useLoadPatientConsentStatus';
// Constants
import { DATE_FORMAT, EMPTY_STRING } from '../../../constants/constants';

/**
 * Renders the "CONSENT STATUS:" line for a Bridge patient. Owns the consent-status fetch (so it
 * only runs once the gate in ConsentStatus has confirmed eligibility).
 *
 * The backend computes ACTIVE/EXPIRED/NONE; this component only formats and styles:
 *  - the consent date renders as neutral `MM/DD/YY` text when Active, and red + bold when Expired;
 *  - the status renders as a Bootstrap `badge` using the existing `alert-success` / `alert-danger`
 *    color classes for the soft pale-green (Active) / pale-red (Expired) look — no custom CSS;
 *  - when NONE (no/incomplete consent) it shows `---` with no badge.
 */
export function ConsentStatusContent() {
  const { id: patientId } = useParams();
  const { consentStatus, loading } = useLoadPatientConsentStatus({ patientId });

  const status = consentStatus ? consentStatus.status : null;
  const isActive = status === 'ACTIVE';
  const isExpired = status === 'EXPIRED';
  const hasDate = (isActive || isExpired) && !!consentStatus.consentDate;

  const dateText = hasDate
    ? moment(consentStatus.consentDate, DATE_FORMAT.FULL_SERVER_WITH_TIME).format(DATE_FORMAT.SHORT)
    : EMPTY_STRING;

  const dateClass = isExpired ? 'text-danger font-weight-bold' : '';
  const badgeColorClass = isActive ? 'alert-success' : 'alert-danger';
  const badgeLabel = isActive ? 'Active' : 'Expired';

  return (
    <div className="text-left mt-2" data-test="summaryStatusInfo_consentStatus">
      <span className="text-uppercase mr-2">Consent status:</span>
      {loading ? (
        // Muted placeholder while the fetch is in flight, so loading is distinct from a settled
        // NONE result (which shows the `---` fallback from the EMPTY_STRING date above).
        <span className="mr-2 text-muted" data-test="summaryStatusInfo_consentStatusLoading">
          Loading&hellip;
        </span>
      ) : (
        <>
          <span
            className={`mr-2${dateClass ? ` ${dateClass}` : ''}`}
            data-test="summaryStatusInfo_consentStatusValue"
          >
            {dateText}
          </span>
          {hasDate && (
            <span
              className={`badge ${badgeColorClass} font-weight-normal px-2 py-1`}
              // .badge defaults to 75%; use 100% of the parent to match the adjacent date text.
              style={{ fontSize: '100%' }}
              data-test="summaryStatusInfo_consentStatusBadge"
            >
              {badgeLabel}
            </span>
          )}
        </>
      )}
    </div>
  );
}

export default ConsentStatusContent;
